/**
 * Weekly Comparison Chart Component
 * 今週と先週の曜日別出席傾向比較折れ線グラフ（ダークモード対応）
 */

import { Card, Text, Group, Badge, useMantineColorScheme } from '@mantine/core';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { TooltipProps } from 'recharts';
import { motion } from 'framer-motion';
import { getGlassStyle } from '../styles/darkModeGlass';
import type { WeeklyComparisonData } from '../types/enhanced';

const MotionCard = motion(Card);

interface TooltipPayloadItem {
  dataKey: string;
  value: number;
  payload: { label?: string };
}

// カスタムツールチップコンポーネント
const CustomTooltip = ({ active, payload }: TooltipProps<number, string>) => {
  if (!active || !payload || payload.length === 0) return null;

  const items = payload as TooltipPayloadItem[];
  const thisWeek = items.find((p) => p.dataKey === '今週')?.value || 0;
  const lastWeek = items.find((p) => p.dataKey === '先週')?.value || 0;
  const diff = thisWeek - lastWeek;

  return (
    <Card
      p="xs"
      shadow="md"
      radius="sm"
      style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(8px)',
        border: '1px solid rgba(0,0,0,0.1)',
      }}
    >
      <Text size="sm" fw={700} mb={4}>
        {payload[0]?.payload?.label}
      </Text>
      <Group gap="xs">
        <Badge size="sm" color="blue">
          今週: {thisWeek}名
        </Badge>
        <Badge size="sm" color="gray">
          先週: {lastWeek}名
        </Badge>
      </Group>
      {diff !== 0 && (
        <Text size="xs" c="dimmed" mt={4}>
          差分: {diff > 0 ? '+' : ''}
          {diff}名
        </Text>
      )}
    </Card>
  );
};

export const WeeklyComparisonChart = ({ data }: { data: WeeklyComparisonData }) => {
  const { colorScheme } = useMantineColorScheme();

  // this_weekとlast_weekをマージ
  const chartData = data.this_week.map((thisWeek, index) => ({
    label: thisWeek.label,
    今週: thisWeek.count,
    先週: data.last_week[index].count,
  }));

  return (
    <MotionCard
      padding="sm"
      radius="sm"
      shadow="sm"
      style={{
        ...getGlassStyle(colorScheme),
        minHeight: '300px',
      }}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Text size="sm" fw={600} mb="xs">
        曜日別出席傾向
      </Text>
      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
          <defs>
            <linearGradient id="colorThisWeek" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#339af0" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#339af0" stopOpacity={0.05} />
            </linearGradient>
            <linearGradient id="colorLastWeek" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#adb5bd" stopOpacity={0.2} />
              <stop offset="95%" stopColor="#adb5bd" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#666" />
          <YAxis tick={{ fontSize: 11 }} stroke="#666" />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Area
            type="monotone"
            dataKey="先週"
            stroke="#adb5bd"
            strokeWidth={2}
            fill="url(#colorLastWeek)"
          />
          <Area
            type="monotone"
            dataKey="今週"
            stroke="#339af0"
            strokeWidth={2}
            fill="url(#colorThisWeek)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </MotionCard>
  );
};
